<template>
  <Dialog
    v-model:visible="isVisible"
    :modal="true"
    :style="{ width: '450px' }"
    :header="dialogConfig.header"
  >
    <div class="p-fluid">
      <p>{{ dialogConfig.message }}</p>
      <p class="font-semibold mt-2">
        {{ currentIntegration?.name }}
      </p>
    </div>
    <template #footer>
      <SpmButton
        :label="dialogConfig.rejectLabel"
        icon="pi pi-times"
        class-style="p-button p-button-secondary"
        :disabled="isLoading"
        @click="close"
      />
      <SpmButton
        :label="dialogConfig.acceptLabel"
        :icon="dialogConfig.acceptIcon"
        :class-style="dialogConfig.acceptClass"
        :loading="isLoading"
        @click="confirm"
      />
    </template>
  </Dialog>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import type { Integration } from '@/composables/useIntegrations';

type ActionType = 'uninstall' | 'activate' | 'deactivate' | 'cancel' | 'install' | 'installOAuth';

interface DialogConfig {
  header: string;
  message: string;
  acceptLabel: string;
  rejectLabel: string;
  acceptIcon: string;
  acceptClass: string;
}

export default defineComponent({
  name: 'IntegrationConfirmDialog',

  components: {
    Dialog,
    SpmButton,
  },

  emits: ['confirm'],

  setup(_, { emit, expose }) {
    const { t } = useI18n();

    // Internal state
    const isVisible = ref(false);
    const isLoading = ref(false);
    const currentIntegration = ref<Integration | null>(null);
    const currentAction = ref<ActionType>('uninstall');

    /**
     * Get dialog configuration based on action type
     */
    const dialogConfig = computed<DialogConfig>(() => {
      switch (currentAction.value) {
        case 'uninstall':
          return {
            header: t('integrations.confirm.uninstall.header'),
            message: t('integrations.confirm.uninstall.message'),
            acceptLabel: t('integrations.confirm.uninstall.acceptLabel'),
            rejectLabel: t('integrations.confirm.uninstall.rejectLabel'),
            acceptIcon: 'pi pi-trash',
            acceptClass: 'p-button p-button-danger',
          };
        case 'activate':
          return {
            header: t('integrations.confirm.activate.header'),
            message: t('integrations.confirm.activate.message'),
            acceptLabel: t('integrations.confirm.activate.acceptLabel'),
            rejectLabel: t('integrations.confirm.activate.rejectLabel'),
            acceptIcon: 'pi pi-check',
            acceptClass: 'p-button p-button-success',
          };
        case 'deactivate':
          return {
            header: t('integrations.confirm.deactivate.header'),
            message: t('integrations.confirm.deactivate.message'),
            acceptLabel: t('integrations.confirm.deactivate.acceptLabel'),
            rejectLabel: t('integrations.confirm.deactivate.rejectLabel'),
            acceptIcon: 'pi pi-times',
            acceptClass: 'p-button p-button-danger',
          };
        case 'cancel':
          return {
            header: t('integrations.confirm.cancel.header'),
            message: t('integrations.confirm.cancel.message'),
            acceptLabel: t('integrations.confirm.cancel.acceptLabel'),
            rejectLabel: t('integrations.confirm.cancel.rejectLabel'),
            acceptIcon: 'pi pi-times',
            acceptClass: 'p-button p-button-danger',
          };
        case 'install':
          return {
            header: t('integrations.confirm.install.header'),
            message: t('integrations.confirm.install.message'),
            acceptLabel: t('integrations.confirm.install.acceptLabel'),
            rejectLabel: t('integrations.confirm.install.rejectLabel'),
            acceptIcon: 'pi pi-download',
            acceptClass: 'p-button p-button-success',
          };
        case 'installOAuth':
          return {
            header: t('integrations.confirm.installOAuth.header'),
            message: t('integrations.confirm.installOAuth.message'),
            acceptLabel: t('integrations.confirm.installOAuth.acceptLabel'),
            rejectLabel: t('integrations.confirm.installOAuth.rejectLabel'),
            acceptIcon: 'pi pi-external-link',
            acceptClass: 'p-button p-button-success',
          };
        default:
          return {
            header: '',
            message: '',
            acceptLabel: '',
            rejectLabel: '',
            acceptIcon: 'pi pi-check',
            acceptClass: 'p-button p-button-primary',
          };
      }
    });

    /**
     * Open the dialog with the given integration and action
     */
    const open = (integration: Integration, action: ActionType) => {
      currentIntegration.value = integration;
      currentAction.value = action;
      isVisible.value = true;
    };

    /**
     * Close the dialog
     */
    const close = () => {
      if (isLoading.value) return; // Prevent closing while loading
      isVisible.value = false;
      isLoading.value = false;
      currentIntegration.value = null;
    };

    /**
     * Confirm the action and emit result
     */
    const confirm = () => {
      if (!currentIntegration.value || isLoading.value) return;

      isLoading.value = true;
      emit('confirm', {
        integration: currentIntegration.value,
        action: currentAction.value,
      });
    };

    /**
     * Finish loading and close dialog (called from parent after action completes)
     */
    const finishLoading = () => {
      isLoading.value = false;
      isVisible.value = false;
      currentIntegration.value = null;
    };

    // Expose methods for parent component
    expose({ open, finishLoading });

    return {
      isVisible,
      isLoading,
      currentIntegration,
      dialogConfig,
      close,
      confirm,
    };
  },
});
</script>
